import { useEffect, useRef, useState } from "react";
import { Hand, Lock, ShieldCheck } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { PreJoinCameraButton, PreJoinMicButton } from "./InCallDeviceButtons";
import { usePreJoinDevices } from "../hooks/useCallDevices";
import { chosenDevices, selectionFromChoices, type ChosenDevices } from "../lib/callDevices";

export interface PreJoinChoices extends ChosenDevices {
  username: string;
  videoEnabled: boolean;
  audioEnabled: boolean;
}

interface PreJoinViewProps {
  roomName: string;
  defaults?: Partial<PreJoinChoices>;
  /** When set, hides the name input and joins as this fixed display name.
   *  Used for authenticated WeCo users — their profile name is the source
   *  of truth and shouldn't be editable in PreJoin. Guests still see the
   *  editable input. */
  staticDisplayName?: string;
  /** True for anonymous guests on a room with waiting_room_enabled=true —
   *  swaps the CTA to "Ask to join" and shows the locked-meeting badge. */
  locked?: boolean;
  /** True only for the room's owner/admin (`room.is_administrable`). Gates
   *  the host wording — skipping the waiting room does not make you the host
   *  (GH-1915). */
  isHost?: boolean;
  onJoin: (choices: PreJoinChoices) => void;
  onCancel?: () => void;
}

/**
 * Pre-join screen — collects username + mic/cam intent before mounting
 * <LiveKitRoom>. Split layout (big preview + heading/CTA column) matching
 * the We-Co design system's meeting flow. Deliberately avoids LiveKit's
 * <PreJoin> (depends on @livekit/components-styles, not imported in the
 * shell) in favor of this Tailwind-native rewrite.
 */
export function PreJoinView({ roomName, defaults, staticDisplayName, locked, isHost, onJoin, onCancel }: PreJoinViewProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState(defaults?.username ?? "");
  const [videoEnabled, setVideoEnabled] = useState(defaults?.videoEnabled ?? true);
  const [audioEnabled, setAudioEnabled] = useState(defaults?.audioEnabled ?? true);
  // Mic, camera and speaker picks — carried into the call on Join (GH-1859).
  const devices = usePreJoinDevices(selectionFromChoices(defaults));
  const camDeviceId = devices.selection.videoinput;
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Maintain a single getUserMedia stream that mirrors the user's current
  // toggles/device choice. Re-acquires whenever videoEnabled or the chosen
  // camera device changes so the preview always reflects intent. Switching
  // the ACTIVE device mid-call (post-join) is a separate follow-up — this
  // only drives the pre-join preview.
  useEffect(() => {
    let cancelled = false;

    const stopVideoTracks = () => {
      streamRef.current?.getVideoTracks().forEach((tr) => tr.stop());
    };

    const start = async () => {
      if (!videoEnabled) {
        stopVideoTracks();
        if (videoRef.current) videoRef.current.srcObject = null;
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: camDeviceId ? { deviceId: { exact: camDeviceId } } : true,
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        stopVideoTracks();
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setDeviceError(null);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof Error && err.name === "NotAllowedError"
            ? t("call.preJoin.errCameraBlocked")
            : t("call.preJoin.errCameraFailed");
        setDeviceError(msg);
      }
    };

    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
  }, [videoEnabled, camDeviceId, t]);

  const effectiveName = staticDisplayName?.trim() ?? username.trim();
  const canJoin = effectiveName.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canJoin) return;
    onJoin({
      username: effectiveName,
      videoEnabled,
      audioEnabled,
      ...chosenDevices(devices.selection),
    });
  };

  return (
    <div className="flex-1 min-h-0 flex items-center justify-center bg-background p-8 overflow-y-auto no-scrollbar pj-wrap">
      <style>{PREJOIN_CSS}</style>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="max-w-4xl w-full pj-grid"
      >
        {/* Preview tile — controls live inside; camera errors sit under the
            frame so they never paint over the mic/camera buttons. */}
        <div className="pj-card">
          <div className="relative w-full aspect-video rounded-3xl shadow-xl">
            <div className="absolute inset-0 rounded-3xl overflow-hidden bg-foreground flex items-center justify-center">
              {videoEnabled ? (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover bg-foreground -scale-x-100"
                />
              ) : (
                <div className="size-24 text-3xl rounded-full bg-neutral-600 text-white flex items-center justify-center font-bold select-none">
                  {(effectiveName.trim().charAt(0) || "?").toUpperCase()}
                </div>
              )}
            </div>

            {locked && (
              <div className="absolute top-4 start-4 flex items-center gap-2 px-3 py-2 bg-black/55 backdrop-blur-sm rounded-xl">
                <Lock className="size-3.5 text-primary" />
                <span className="text-white text-xs font-bold tracking-wide">
                  {t("call.preJoin.lockedMeeting")}
                </span>
              </div>
            )}

            <div className="absolute bottom-5 inset-x-0 flex justify-center gap-3">
              <PreJoinMicButton
                micOn={audioEnabled}
                onToggleMic={() => setAudioEnabled((v) => !v)}
                devices={devices}
                big
              />
              <PreJoinCameraButton
                cameraOn={videoEnabled}
                onToggleCamera={() => setVideoEnabled((v) => !v)}
                devices={devices}
                big
              />
            </div>
          </div>
          {deviceError && (
            <p className="mt-2 text-center text-[11px] font-medium text-red-500 leading-snug px-2">
              {deviceError}
            </p>
          )}
        </div>

        {/* Heading + CTA column */}
        <div className="text-center lg:text-start pj-side">
          <h2 className="text-2xl font-black tracking-tight mb-2 text-foreground">
            {locked
              ? t("call.preJoin.readyToAskToJoin")
              : isHost
                ? t("call.preJoin.readyToStart")
                : t("call.preJoin.readyToJoin")}
          </h2>
          <p className="text-muted-foreground font-medium mb-1 truncate">{roomName}</p>
          {locked ? (
            <p className="text-sm text-muted-foreground mb-6 flex items-center justify-center lg:justify-start gap-2 pj-note">
              <ShieldCheck className="size-3.5 text-primary shrink-0" />
              {t("call.preJoin.hostWillAdmit")}
            </p>
          ) : isHost ? (
            <p className="text-sm text-muted-foreground mb-6">{t("call.preJoin.youAreHost")}</p>
          ) : (
            <div className="mb-6" />
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-xs mx-auto lg:mx-0 pj-actions">
            {/* Guests name themselves here; signed-in users join under their
                profile name (no field, as in the design). */}
            {!staticDisplayName && (
              <input
                id="prejoin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("call.preJoin.namePlaceholder")}
                aria-label={t("call.preJoin.yourName")}
                autoFocus
                className="w-full h-12 px-4 border border-border rounded-2xl bg-transparent outline-none focus:border-primary text-sm font-medium text-foreground placeholder:text-muted-foreground"
              />
            )}
            <button
              type="submit"
              disabled={!canJoin}
              className="h-12 bg-primary text-primary-foreground font-bold rounded-2xl hover:scale-[1.02] transition-transform shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {locked && <Hand className="size-[17px]" />}
              {locked
                ? t("call.preJoin.askToJoin")
                : isHost
                  ? t("call.preJoin.startMeeting")
                  : t("call.preJoin.joinNow")}
            </button>
            {/* Signed out here — offer to come back as the account holder. */}
            {!staticDisplayName && (
              <a
                href={signInHref()}
                className="text-sm font-medium text-primary hover:underline text-center"
              >
                {t("call.preJoin.signInToJoin")}
              </a>
            )}
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="h-12 border border-border font-bold rounded-2xl hover:bg-muted transition-colors text-foreground"
              >
                {t("call.preJoin.cancel")}
              </button>
            )}
          </form>
        </div>
      </motion.div>
    </div>
  );
}

/** Login URL that brings the visitor back to the page they are on. */
export function signInHref(): string {
  const back = window.location.pathname + window.location.search;
  return `/login?next=${encodeURIComponent(back)}`;
}

/** weco2/call.jsx pre-join container queries (`.pj-*`): preview + side
 *  column from 761px of module width, one centred column below it. */
const PREJOIN_CSS =
  ".pj-wrap{container-type:inline-size;align-items:safe center}" +
  ".pj-grid{display:grid;grid-template-columns:1fr;gap:1.5rem;align-items:stretch}" +
  "@container (min-width:761px){.pj-grid{grid-template-columns:1.4fr 1fr;gap:2.5rem;align-items:center}}" +
  "@container (max-width:760px){.pj-card{margin-inline:-.75rem}.pj-side{text-align:center!important}" +
  ".pj-note{justify-content:center!important}" +
  ".pj-actions{width:100%;max-width:none!important;margin-left:0!important;margin-right:0!important}}";
